import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCategoryName } from '../../utils/categoryTranslation';
import { 
  CheckCircle, 
  Download, 
  CalendarToday, 
  LocationOn, 
  CreditCard, 
  Description, 
  Schedule, 
  Phone, 
  Email, 
  Person,
  Print,
  Share,
  Home,
  ExpandMore as ExpandMoreIcon,
  PhotoLibrary,
  Business,
  Chat as ChatIcon
} from '@mui/icons-material';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Button
} from '@mui/material';
import { formatUnifiedPrice } from '../../utils/priceCalculatorUnified';
import { getTokenAuth } from '../../utils/authUtils';
import api from '../../services/api';
import { RESERVATION_STATUS, PAYMENT_STATUS, PAYMENT_TYPES } from '../../constants/statusCodes';
import {
  getReservationStatusLabel,
  getPaymentStatusLabel,
  getStatusIconColor
} from '../../utils/statusUtils';
import StatusChip from '../common/StatusChip';
import ImageCarousel from '../common/ImageCarousel';
import RideShareMap from '../listing/RideShareMap';
import ReadOnlyMap from '../common/ReadOnlyMap';
import '../../styles/BookingConfirmation.css';

// Fonction de secours pour formater les prix si l'importation échoue
const safeFormatPrice = (amount, currency = 'EUR') => {
  if (amount === null || amount === undefined) return 'N/A';
  
  try {
    // Essayer d'utiliser la fonction importée
    return formatUnifiedPrice(amount, currency);
  } catch (error) {
    // Fallback simple si la fonction importée ne fonctionne pas
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(amount);
    } catch (error) {
      // Dernière chance avec fallback générique
      const currencySymbol = currency === 'USD' ? '$' : 
                            currency === 'GBP' ? '£' : 
                            currency === 'EUR' ? '€' : 
                            currency || '€';
      return `${amount} ${currencySymbol}`;
    }
  }
};

// Fonction pour obtenir le prix principal d'un listing
const getMainPrice = (listing) => {
  if (!listing) return 0;
  
  // Priorité : daily > hourly > weekly > monthly
  if (listing.price_daily && listing.price_daily > 0) {
    return {
      amount: listing.price_daily,
      period: 'jour',
      periodKey: 'perDay'
    };
  } else if (listing.price_hourly && listing.price_hourly > 0) {
    return {
      amount: listing.price_hourly,
      period: 'heure',
      periodKey: 'perHour'
    };
  } else if (listing.price_weekly && listing.price_weekly > 0) {
    return {
      amount: listing.price_weekly,
      period: 'semaine',
      periodKey: 'perWeek'
    };
  } else if (listing.price_monthly && listing.price_monthly > 0) {
    return {
      amount: listing.price_monthly,
      period: 'mois',
      periodKey: 'perMonth'
    };
  }
  
  return {
    amount: 0,
    period: 'jour',
    periodKey: 'perDay'
  };
};

const DAY_IN_MS = 1000 * 60 * 60 * 24;

const toDateKey = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const areSameCalendarDay = (startValue, endValue) => {
  const startKey = toDateKey(startValue);
  const endKey = toDateKey(endValue);
  return Boolean(startKey && endKey && startKey === endKey);
};

const getCalendarDayDiff = (startValue, endValue) => {
  const startKey = toDateKey(startValue);
  const endKey = toDateKey(endValue);

  if (!startKey || !endKey) return null;

  const start = new Date(`${startKey}T00:00:00.000Z`);
  const end = new Date(`${endKey}T00:00:00.000Z`);
  return Math.max(1, Math.round((end - start) / DAY_IN_MS));
};

const BookingConfirmation = () => {
  const { t } = useTranslation(['common', 'booking']);
  const getCategoryName = useCategoryName();
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);

  // États pour la gestion des accordéons
  const [expandedPanels, setExpandedPanels] = useState({
    listing: true,
    description: false, // Sous-accordéon description
    address: false, // Sous-accordéon adresse
    owner: false, // Sous-accordéon propriétaire
    reservation: true,
    payment: false,
    documents: false,
    nextSteps: false
  });

  // Fonction pour gérer l'ouverture/fermeture des accordéons
  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panel]: isExpanded
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common:notSpecified', 'Non spécifié');
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateWithTime = (dateString) => {
    if (!dateString) return t('common:notSpecified', 'Non spécifié');
    try {
      const date = new Date(dateString);
      const dateFormatted = date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const timeFormatted = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `${dateFormatted} ${timeFormatted}`;
    } catch (error) {
      return dateString;
    }
  };

  const bookingMode = reservation?.booking_mode_snapshot || reservation?.listing?.booking_mode || 'date_range';
  const offerType = reservation?.offer_type_snapshot || reservation?.listing?.offer_type || 'equipment';
  const isProductOrder = reservation?.listing?.booking_settings?.is_product_order === true || offerType === 'product';
  const isSlotBooking = ['time_slot', 'time_slot_capacity'].includes(bookingMode);
  const isServiceOffer = offerType === 'service' || (isSlotBooking && !isProductOrder);
  const hasReservationTimes = Boolean(reservation?.start_at || reservation?.end_at);
  const reservationStart = reservation?.start_at || reservation?.start_date;
  const reservationEnd = reservation?.end_at || reservation?.end_date;
  const mainPrice = getMainPrice(reservation?.listing);
  const nextStepsVariant = isProductOrder ? 'product' : (isServiceOffer || isSlotBooking ? 'service' : 'rental');

  const getOfferSectionTitle = () => {
    return t('booking:confirmation.listing.offerTitle');
  };

  const getCounterpartyLabel = () => {
    return isProductOrder
      ? t('booking:confirmation.listing.sellerLabel')
      : t('booking:confirmation.listing.providerLabel');
  };

  const getContactNote = () => {
    if (isProductOrder) return t('booking:confirmation.listing.contactNotes.product');
    if (isServiceOffer || isSlotBooking) return t('booking:confirmation.listing.contactNotes.service');
    return t('booking:confirmation.listing.contactNotes.rental');
  };

  const getReservationStatusChipLabel = (status) => {
    return t(`booking:confirmation.statuses.reservation.${status}`, getReservationStatusLabel(status));
  };

  const getPaymentStatusChipLabel = (status) => {
    return t(`booking:confirmation.statuses.payment.${status}`, getPaymentStatusLabel(status));
  };

  const getPaymentTypeLabel = (paymentType) => {
    return t(
      `booking:confirmation.payment.types.${paymentType}`,
      paymentType || t('booking:confirmation.payment.defaultType')
    );
  };

  const getPrimaryPriceDisplay = () => {
    const currency = reservation?.currency || reservation?.listing?.currency;


      const amount = reservation?.total_amount || reservation?.total_price  || mainPrice.amount;
      return amount ? safeFormatPrice(amount, currency) : t('booking:confirmation.notAvailable');
    return t('booking:confirmation.notAvailable');
  };

  const getDurationDisplay = () => {
    if (!reservationStart || !reservationEnd) {
      return t('booking:confirmation.notAvailable');
    }

    if (areSameCalendarDay(reservationStart, reservationEnd)) {
      return t('booking:confirmation.reservation.sameDay');
    }

    const diffDays = getCalendarDayDiff(reservationStart, reservationEnd);
    if (!diffDays) {
      return t('booking:confirmation.notAvailable');
    }

    return t('booking:confirmation.reservation.durationCount', { count: diffDays });
  };

  useEffect(() => {
    if (reservationId) {
      fetchReservationDetails();
    } else {
      setError(t('booking:confirmation.notFound'));
      setLoading(false);
    }
  }, [reservationId]);

  const fetchReservationDetails = async () => {
    try {
      setLoading(true);
      const token = getTokenAuth();
      
      console.log('🔍 Récupération des détails de la réservation:', reservationId);
      
      // Vérifier si le token est présent
      if (!token) {
        console.error('❌ Token d\'authentification manquant!');
        setError(t('booking:confirmation.errors.authMissing'));
        setLoading(false);
        return;
      }
      
      // Utiliser l'URL complète avec le préfixe API
      let apiUrl = `${process.env.REACT_APP_API_BASE_URL}`;
      // Si l'URL ne contient pas déjà /api, l'ajouter
      if (!apiUrl.includes('/api')) {
        apiUrl = `${apiUrl}/api`;
      }
      
      const response = await fetch(`${apiUrl}/reservations/${reservationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📋 Statut de la réponse:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erreur API réservation:', response.status, errorText);
        throw new Error(t('booking:confirmation.errors.fetchError'));
      }

      const data = await response.json();
      console.log('📋 Données de réservation reçues:', data);
      
      if (data && data.data) {
        const reservationData = data.data.reservation || data.data;
        console.log('Structure de la réservation:', reservationData);
        setReservation(reservationData);
      } else {
        console.error('Format de réponse inattendu pour la réservation:', data);
        throw new Error(t('booking:confirmation.errors.invalidFormat'));
      }

      // Récupérer les documents associés
      await fetchDocuments();
      
    } catch (error) {
      console.error('❌ Erreur lors de la récupération de la réservation:', error);
      setError(error.message || t('booking:confirmation.errors.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const token = getTokenAuth();
      console.log('📄 Récupération des documents pour:', reservationId);
      
      // Vérifier si le token est présent
      if (!token) {
        console.error('❌ Token d\'authentification manquant pour les documents!');
        return;
      }
      
      // Utiliser l'URL complète avec le préfixe API
      let apiUrl = `${process.env.REACT_APP_API_BASE_URL}`;
      // Si l'URL ne contient pas déjà /api, l'ajouter
      if (!apiUrl.includes('/api')) {
        apiUrl = `${apiUrl}/api`;
      }
      
      const response = await fetch(`${apiUrl}/documents/reservation/${reservationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📄 Statut de la réponse documents:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📄 Documents reçus:', data);
        
        if (data.data && Array.isArray(data.data)) {
          console.log('Structure des documents:', data.data.map(d => ({
            id: d.document_id || d.id,
            file_name: d.file_name || d.fileName,
            type: d.type
          })));
          setDocuments(data.data || []);
        } else if (data.documents && Array.isArray(data.documents)) {
          console.log('Structure alternative des documents:', data.documents);
          setDocuments(data.documents || []);
        } else {
          console.error('Format de réponse inattendu pour les documents:', data);
          setDocuments([]);
        }
      } else {
        console.error('Erreur API lors de la récupération des documents:', response.status);
        const errorText = await response.text();
        console.error('Détails de l\'erreur documents:', errorText);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des documents:', error);
    }
  };

  const downloadDocument = async (documentId, fileName) => {
    try {
      console.log('🔍 Téléchargement du document:', { documentId, fileName });
      const token = getTokenAuth();
      
      if (!token) {
        console.error('❌ Token d\'authentification manquant pour le téléchargement!');
        alert('Erreur: Token d\'authentification manquant');
        return;
      }
      
      if (!documentId) {
        console.error('❌ ID de document manquant pour le téléchargement!');
        alert('Erreur: ID de document manquant');
        return;
      }
      
      console.log('🔗 Téléchargement via API axios...');
      
      // Utiliser l'instance axios configurée avec les intercepteurs
      const response = await api.get(`/documents/${documentId}/download`, {
        responseType: 'blob'  // Important pour les fichiers binaires
      });

      console.log('📄 Statut de la réponse téléchargement:', response.status);
      
      if (response.status === 200) {
        console.log('✅ Document récupéré avec succès');
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'document';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        console.log('✅ Téléchargement initié avec succès');
      } else {
        console.error('❌ Erreur lors du téléchargement:', {
          status: response.status,
          statusText: response.statusText,
          data: response.data
        });
        alert(`Erreur lors du téléchargement: ${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ Erreur lors du téléchargement:', error);
      alert(`Erreur lors du téléchargement: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="booking-confirmation-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t('booking:confirmation.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-confirmation-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h2>{t('booking:confirmation.error')}</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            {t('booking:confirmation.actions.backToHome')}
          </button>
        </div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="booking-confirmation-container">
        <div className="empty-state">
          <h2>{t('booking:confirmation.reservationNotFound')}</h2>
          <p>{t('booking:confirmation.cannotLoadDetails')}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            {t('booking:confirmation.actions.backToHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-confirmation-container">
      <div className="confirmation-wrapper">
        
        {/* Header Success */}
        <div className="confirmation-header">
          <div className="success-animation">
            <CheckCircle className="success-icon" />
          </div>
          <h1>{t('booking:confirmation.success')}</h1>
          <p className="success-subtitle">
            {t('booking:confirmation.congratulations')}
          </p>
          <div className="reservation-number">
            <strong>{t('booking:confirmation.reservationNumber')} : {reservation.id ? reservation.id.slice(0, 8).toUpperCase() : t('booking:confirmation.notAvailable')}</strong>
          </div>
        </div>

        {/* Actions rapides */}
        <div className="quick-actions">
          <button onClick={() => window.print()} className="action-btn print-btn">
            <Print />
            {t('booking:confirmation.actions.print')}
          </button>
          <button onClick={() => navigate('/')} className="action-btn home-btn">
            <Home />
            {t('booking:confirmation.actions.home')}
          </button>
          <button onClick={() => {
            if (navigator.share) {
              navigator.share({
                title: t('booking:confirmation.actions.shareTitle'),
                text: t('booking:confirmation.actions.shareText', {
                  title: reservation.listing?.title || t('booking:confirmation.actions.shareFallbackTitle')
                }),
                url: window.location.href
              });
            }
          }} className="action-btn share-btn">
            <Share />
            {t('booking:confirmation.actions.share')}
          </button>
        </div>

        <div className="confirmation-content">
          
          {/* Accordéon principal - Informations de l'objet loué */}
          <Accordion 
            expanded={expandedPanels.listing} 
            onChange={handlePanelChange('listing')}
            sx={{ 
              mb: 3,
              boxShadow: 2,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <Description sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {getOfferSectionTitle()}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {reservation.listing ? (
                <Box>
                  {/* Section des informations principales - Toujours visible */}
                  <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
                    {/* Titre et catégorie */}
                    <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', color: '#1f2937' }}>
                      {reservation.listing.title || t('booking:confirmation.listing.titleNotAvailable')}
                    </Typography>
                    
                    {reservation.listing.category && (
                      <Box sx={{ mb: 3 }}>
                        <Chip 
                          label={`${t('booking:confirmation.listing.category')}: ${getCategoryName(reservation.listing.category) || t('booking:confirmation.listing.notSpecified')}`}
                          sx={{ 
                            bgcolor: '#6200ee', 
                            color: 'white',
                            fontWeight: 'medium',
                            '& .MuiChip-label': {
                              px: 2,
                              py: 1
                            }
                          }}
                        />
                      </Box>
                    )}

                    {/* Créneau / dates de la réservation */}
                    {(reservation?.start_at || reservation?.end_at || reservation?.start_date || reservation?.end_date) && (() => {
                      const dateLabel = hasReservationTimes
                        ? `${formatDateWithTime(reservation.start_at || reservation.start_date)} — ${formatDateWithTime(reservation.end_at || reservation.end_date)}`
                        : `${formatDate(reservation.start_date)} — ${formatDate(reservation.end_date)}`;
                      return (
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #a5d6a7' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: reservation?.seats_requested > 1 ? 0.5 : 0 }}>
                            <Schedule sx={{ color: '#2e7d32', fontSize: 20 }} />
                            <Typography variant="body2" fontWeight="600" color="#1b5e20">
                              {dateLabel}
                            </Typography>
                          </Box>
                          {reservation?.seats_requested > 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5, display: 'block' }}>
                              {t('booking:confirmation.reservation.seatsRequested', '{{count}} places réservées', { count: reservation.seats_requested })}
                            </Typography>
                          )}

                          {reservation?.quantity_requested > 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5, display: 'block' }}>
                              {t('booking:confirmation.reservation.qtyRequested', '{{count}} quantité réservée', { count: reservation.quantity_requested })}
                            </Typography>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Prix */}
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '2px solid #e2e8f0' }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: '#1f2937' }}>
                        <CreditCard sx={{ mr: 1, color: '#6200ee' }} />
                        {getPrimaryPriceDisplay()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Carrousel d'images - Toujours visible dans l'accordéon principal */}
                  {(() => {
                    const UPLOADS_BASE = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:5000';
                    const getProductImageUrl = (path) => {
                      if (!path) return null;
                      if (path.startsWith('http')) return path;
                      return `${UPLOADS_BASE}${path.startsWith('/') ? path : '/' + path}`;
                    };
                    const carouselImages = isProductOrder && reservation.listing.product?.images?.length > 0
                      ? reservation.listing.product.images.map(getProductImageUrl).filter(Boolean)
                      : reservation.listing.images?.map(img => img.image_url || img) || [];
                    return carouselImages.length > 0 ? (
                      <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
                        <Box sx={{ 
                          bgcolor: '#f8fafc', 
                          p: 2,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <PhotoLibrary sx={{ mr: 1, color: '#6200ee' }} />
                          <Typography variant="h6" sx={{ fontWeight: 'medium', color: '#1f2937' }}>
                            {t('booking:confirmation.listing.photos')}
                          </Typography>
                        </Box>
                        <Box sx={{ bgcolor: '#fff' }}>
                          <ImageCarousel
                            images={carouselImages}
                            height={400}
                            showControls={true}
                            showIndicators={true}
                            showCounter={true}
                            alt={reservation.listing.title}
                          />
                        </Box>
                      </Box>
                    ) : null;
                  })()}

                  {/* Sous-accordéon - Description (si elle est grande > 200 caractères) */}
                  {reservation.listing.description && reservation.listing.description.length > 200 && (
                    <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
                      <Accordion 
                        expanded={expandedPanels.description} 
                        onChange={handlePanelChange('description')}
                        sx={{ 
                          boxShadow: 0,
                          '&:before': { display: 'none' },
                          borderRadius: '0 !important'
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            background: '#f8fafc',
                            color: '#374151',
                            borderBottom: '1px solid #e2e8f0',
                            '& .MuiAccordionSummary-content': {
                              alignItems: 'center'
                            },
                            '&:hover': {
                              background: '#f1f5f9'
                            }
                          }}
                        >
                          <Description sx={{ mr: 1, color: '#6200ee' }} />
                          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                            {t('booking:confirmation.listing.descriptionDetailed')}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3, bgcolor: '#fff' }}>
                          <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#374151' }}>
                            {reservation.listing.description}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}

                  {/* Description courte (si moins de 200 caractères) - Toujours visible */}
                  {reservation.listing.description && reservation.listing.description.length <= 200 && (
                    <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Description sx={{ mr: 1, color: '#6200ee' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'medium', color: '#1f2937' }}>
                          {t('booking:confirmation.listing.description')}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ lineHeight: 1.7, color: '#374151' }}>
                        {reservation.listing.description}
                      </Typography>
                    </Box>
                  )}

                  {/* Sous-accordéon - Adresse */}
                  {reservation.listing.address && (
                    <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
                      <Accordion 
                        expanded={expandedPanels.address} 
                        onChange={handlePanelChange('address')}
                        sx={{ 
                          boxShadow: 0,
                          '&:before': { display: 'none' },
                          borderRadius: '0 !important'
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            background: '#f8fafc',
                            color: '#374151',
                            borderBottom: '1px solid #e2e8f0',
                            '& .MuiAccordionSummary-content': {
                              alignItems: 'center'
                            },
                            '&:hover': {
                              background: '#f1f5f9'
                            }
                          }}
                        >
                          <LocationOn sx={{ mr: 1, color: '#6200ee' }} />
                          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                            {t('booking:confirmation.listing.addressDetails')}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3, bgcolor: '#fff' }}>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1f2937' }}>
                            {t('booking:confirmation.listing.locationLabel')}
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            lineHeight: 1.7, 
                            color: '#374151',
                            p: 2,
                            bgcolor: '#f8fafc',
                            borderRadius: 2,
                            border: '1px solid #e2e8f0',
                            mb: 3
                          }}>
                            {reservation.listing.address}
                          </Typography>
                          
                          {/* Map Integration */}
                          <Box sx={{ mb: 3 }}>
                            {reservation.listing.offer_type === 'ride_share' ? (
                              <Box>
                                <Typography variant="subtitle1" gutterBottom fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#1f2937' }}>
                                   🚗 {t('common:listing.address.rideShareMapTitle', 'Carte du trajet')}
                                </Typography>
                                <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                  <RideShareMap 
                                     startPosition={reservation.listing.latitude && reservation.listing.longitude ? [reservation.listing.latitude, reservation.listing.longitude] : null}
                                     endPosition={reservation.listing.booking_settings?.destinationLat && reservation.listing.booking_settings?.destinationLng ? [reservation.listing.booking_settings.destinationLat, reservation.listing.booking_settings.destinationLng] : null}
                                     readOnly={true}
                                     height={300}
                                  />
                                </Box>
                                {(reservation.listing.booking_settings?.destinationAddress) && (
                                   <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                     <Typography variant="body2" color="text.secondary">
                                       <strong>{t('common:listing.map.arrival', 'Arrivée')}:</strong> {reservation.listing.booking_settings.destinationAddress}
                                     </Typography>
                                   </Box>
                                )}
                              </Box>
                            ) : (
                                (reservation.listing.latitude && reservation.listing.longitude) && (
                                  <Box sx={{ height: 300, mt: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                    <ReadOnlyMap
                                      latitude={reservation.listing.latitude}
                                      longitude={reservation.listing.longitude}
                                      address={reservation.listing.address}
                                      height={300}
                                      zoom={15}
                                      showAddress={false}
                                    />
                                  </Box>
                                )
                            )}
                          </Box>

                          {/* Informations supplémentaires sur l'adresse si disponibles */}
                          {(reservation.listing.city || reservation.listing.postal_code || reservation.listing.country) && (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1f2937' }}>
                                {t('booking:confirmation.listing.locationDetails')}
                              </Typography>
                              <List dense>
                                {reservation.listing.city && (
                                  <ListItem sx={{ px: 0 }}>
                                    <ListItemIcon>
                                      <Business sx={{ color: '#6200ee' }} />
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={`${t('booking:confirmation.listing.city')}: ${reservation.listing.city}`}
                                      sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                                    />
                                  </ListItem>
                                )}
                                {reservation.listing.postal_code && (
                                  <ListItem sx={{ px: 0 }}>
                                    <ListItemIcon>
                                      <LocationOn sx={{ color: '#6200ee' }} />
                                    </ListItemIcon>
                                    <ListItemText 
                                      primary={`${t('booking:confirmation.listing.postalCode')}: ${reservation.listing.postal_code}`}
                                      sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                                    />
                                  </ListItem>
                                )}
                              </List>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}

                  {/* Sous-accordéon - Coordonnées du propriétaire */}
                  {reservation.listing.owner && (
                    <Box>
                      <Accordion 
                        expanded={expandedPanels.owner} 
                        onChange={handlePanelChange('owner')}
                        sx={{ 
                          boxShadow: 0,
                          '&:before': { display: 'none' },
                          borderRadius: '0 !important'
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            background: '#f8fafc',
                            color: '#374151',
                            '& .MuiAccordionSummary-content': {
                              alignItems: 'center'
                            },
                            '&:hover': {
                              background: '#f1f5f9'
                            }
                          }}
                        >
                          <Person sx={{ mr: 1, color: '#6200ee' }} />
                          <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                            {t('booking:confirmation.listing.partnerContact')}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 3, bgcolor: '#fff' }}>
                          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#1f2937' }}>
                            {t('booking:confirmation.listing.contactPartner', { role: getCounterpartyLabel() })}
                          </Typography>
                          
                          <List>
                            <ListItem sx={{ px: 0, mb: 2 }}>
                              <ListItemIcon>
                                <Person sx={{ color: '#6200ee' }} />
                              </ListItemIcon>
                              <ListItemText 
                                primary={reservation.listing.owner.name || t('common:notSpecified', 'Non spécifié')}
                                secondary={t('booking:confirmation.listing.partnerName', { role: getCounterpartyLabel() })}
                                sx={{ 
                                  '& .MuiListItemText-primary': { 
                                    fontWeight: 'bold', 
                                    fontSize: '1.1rem',
                                    color: '#1f2937'
                                  },
                                  '& .MuiListItemText-secondary': {
                                    color: '#6b7280'
                                  }
                                }}
                              />
                            </ListItem>
                            
                            {reservation.listing.owner.email && (
                              <ListItem sx={{ px: 0, mb: 2 }}>
                                <ListItemIcon>
                                  <ChatIcon sx={{ color: '#6200ee' }} />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Button
                                      component={RouterLink}
                                      to={`/reservations/${reservationId}`}
                                      variant="contained"
                                      startIcon={<ChatIcon />}
                                      sx={{
                                        background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                                        color: '#fff',
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                          background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                                        }
                                      }}
                                    >
                                      {t('booking:confirmation.listing.messagingLink')}
                                    </Button>
                                  }
                                  secondary={t('booking:confirmation.listing.messagingLinkHint')}
                                  sx={{
                                    '& .MuiListItemText-secondary': { color: '#6b7280', mt: 0.5 }
                                  }}
                                />
                              </ListItem>
                            )}
                          </List>

                          <Box sx={{ 
                            mt: 3, 
                            p: 2, 
                            bgcolor: '#eff6ff', 
                            borderRadius: 2,
                            border: '1px solid #bfdbfe'
                          }}>
                            <Typography variant="body2" sx={{ 
                              color: '#1e40af',
                              fontStyle: 'italic'
                            }}>
                              {getContactNote()}
                            </Typography>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body1" sx={{ textAlign: 'center', py: 3, color: '#6b7280', p: 3 }}>
                  {t('booking:confirmation.listing.infoNotAvailable')}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Accordéon - Détails de la réservation */}
          <Accordion 
            expanded={expandedPanels.reservation} 
            onChange={handlePanelChange('reservation')}
            sx={{ 
              mb: 3,
              boxShadow: 2,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <Schedule sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {t('booking:confirmation.reservation.title')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {/* Bloc récapitulatif - Dates et Prix */}
              {(reservation?.start_at || reservation?.end_at || reservation?.start_date || reservation?.end_date) && (() => {
                const dateLabel = hasReservationTimes
                  ? `${formatDateWithTime(reservation.start_at || reservation.start_date)} — ${formatDateWithTime(reservation.end_at || reservation.end_date)}`
                  : `${formatDate(reservation.start_date)} — ${formatDate(reservation.end_date)}`;
                return (
                  <Box sx={{ mb: 3 }}>
                    {/* Dates avec heure */}
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #a5d6a7' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule sx={{ color: '#2e7d32', fontSize: 20 }} />
                        <Typography variant="body2" fontWeight="600" color="#1b5e20">
                          {dateLabel}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Prix */}
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '2px solid #e2e8f0' }}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', color: '#1f2937' }}>
                        <CreditCard sx={{ mr: 1, color: '#6200ee' }} />
                        {getPrimaryPriceDisplay()}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}

              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: getStatusIconColor(reservation.status) }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('common:common.status', 'Statut')}
                    secondary={
                      <StatusChip 
                        status={reservation.status}
                        type="reservation"
                        label={getReservationStatusChipLabel(reservation.status)}
                        sx={{ mt: 0.5 }}
                      />
                    }
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CalendarToday sx={{ color: '#6200ee' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('common:startDate', 'Date de début')}
                    secondary={hasReservationTimes ? formatDateWithTime(reservationStart) : formatDate(reservationStart)}
                    sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <CalendarToday sx={{ color: '#6200ee' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('common:endDate', 'Date de fin')}
                    secondary={hasReservationTimes ? formatDateWithTime(reservationEnd) : formatDate(reservationEnd)}
                    sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <Schedule sx={{ color: '#6200ee' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('booking:confirmation.reservation.duration')}
                    secondary={getDurationDisplay()}
                    sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                  />
                </ListItem>
              </List>

              {reservation.codes && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#eff6ff', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#1e40af' }}>
                    {t('booking:confirmation.reservation.accessCodes')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {reservation.codes.client && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{t('booking:confirmation.reservation.clientCode')}</Typography>
                        <Chip 
                          label={reservation.codes.client}
                          sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                        />
                      </Box>
                    )}
                    {reservation.codes.owner && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{t('booking:confirmation.reservation.ownerCode')}</Typography>
                        <Chip 
                          label={reservation.codes.owner}
                          sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Accordéon - Informations de paiement */}
          <Accordion 
            expanded={expandedPanels.payment} 
            onChange={handlePanelChange('payment')}
            sx={{ 
              mb: 3,
              boxShadow: 2,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <CreditCard sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {t('booking:confirmation.payment.title')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {reservation.payment_history && reservation.payment_history.filter(payment => payment.payment_type !== PAYMENT_TYPES.DEPOSIT_REFUND).length > 0 ? (
                <Box>
                  {reservation.payment_history.filter(payment => payment.payment_type !== PAYMENT_TYPES.DEPOSIT_REFUND).map((payment, index) => (
                    <Box key={payment.id || `payment-${index}`} sx={{ mb: 2, p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {getPaymentTypeLabel(payment.payment_type)}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          {safeFormatPrice(payment.amount || 0, reservation.currency || reservation.listing?.currency)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <StatusChip 
                          status={payment.status}
                          type="payment"
                          label={getPaymentStatusChipLabel(payment.status)}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(payment.transaction_date || payment.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#eff6ff', borderRadius: 2, borderTop: '2px solid #3b82f6' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e40af' }}>
                        {t('booking:confirmation.payment.totalPaid')}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                        {safeFormatPrice(reservation.total_amount || reservation.total_price || 0, reservation.currency || reservation.listing?.currency)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <CreditCard sx={{ color: '#6200ee' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('booking:confirmation.payment.amount')}
                      secondary={
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          {reservation.total_amount || reservation.total_price ? 
                            safeFormatPrice(reservation.total_amount || reservation.total_price, reservation.currency || reservation.listing?.currency) : 'N/A'}
                        </Typography>
                      }
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircle sx={{ color: '#10b981' }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={t('booking:confirmation.payment.status')}
                      secondary={
                        <StatusChip 
                          status={reservation.payment_status}
                          type="payment"
                          label={getPaymentStatusChipLabel(reservation.payment_status)}
                          sx={{ mt: 0.5 }}
                        />
                      }
                    />
                  </ListItem>
                  
                  {reservation.payment && (
                    <ListItem>
                      <ListItemIcon>
                        <CalendarToday sx={{ color: '#6200ee' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={t('booking:confirmation.payment.paymentDate')}
                        secondary={formatDate(reservation.payment.created_at || reservation.payment.date)}
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </AccordionDetails>
          </Accordion>

          {/* Accordéon - Documents */}
          {documents && documents.length > 0 && (
            <Accordion 
              expanded={expandedPanels.documents} 
              onChange={handlePanelChange('documents')}
              sx={{ 
                mb: 3,
                boxShadow: 2,
                '&:before': { display: 'none' },
                borderRadius: '8px !important',
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                  color: 'white',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center'
                  },
                  '&:hover': {
                    background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                  }
                }}
              >
                <Description sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                  {t('booking:confirmation.documents.title')} ({documents.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <List>
                  {documents.map((doc, index) => {
                    // Identifier l'ID correct du document
                    const docId = doc.document_id || doc.id || doc.legal_document_id;
                    const docName = doc.file_name || doc.fileName || doc.name || t('booking:confirmation.documents.defaultName');
                    const docType = doc.type || t('booking:confirmation.listing.notSpecified');
                    
                    console.log('📄 Document info:', { docId, docName, docType, originalDoc: doc });
                    
                    return (
                      <ListItem 
                        key={docId || index} 
                        sx={{ 
                          mb: 1, 
                          p: 2, 
                          bgcolor: '#f9fafb', 
                          borderRadius: 2,
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        <ListItemIcon>
                          <Description sx={{ color: '#6200ee' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={docName}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {t('booking:confirmation.documents.typeLabel')} {docType}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {t('booking:confirmation.documents.idLabel')} {docId || t('booking:confirmation.documents.notDefined')}
                              </Typography>
                              {doc.created_at && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {t('common:createdAt')} {formatDate(doc.created_at)}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium' } }}
                        />
                        <Box sx={{ ml: 2 }}>
                          <button 
                            onClick={() => {
                              console.log('🎯 Tentative de téléchargement:', { docId, docName });
                              downloadDocument(docId, docName);
                            }}
                            className="action-btn"
                            disabled={!docId}
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              padding: '0.5rem 1rem',
                              background: docId ? 
                                'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)' : 
                                '#9ca3af',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: docId ? 'pointer' : 'not-allowed',
                              fontSize: '0.875rem',
                              fontWeight: 'medium',
                              opacity: docId ? 1 : 0.6
                            }}
                            title={docId ? t('booking:confirmation.documents.downloadTitle') : t('booking:confirmation.documents.missingDocumentId')}
                          >
                            <Download style={{ fontSize: '1rem' }} />
                            {docId ? t('booking:confirmation.documents.download') : t('booking:confirmation.documents.unavailable')}
                          </button>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Accordéon - Prochaines étapes */}
          <Accordion 
            expanded={expandedPanels.nextSteps} 
            onChange={handlePanelChange('nextSteps')}
            sx={{ 
              mb: 3,
              boxShadow: 2,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              overflow: 'hidden'
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
                color: 'white',
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center'
                },
                '&:hover': {
                  background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
                }
              }}
            >
              <Schedule sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                {t('booking:confirmation.nextSteps.title')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <List>
                <ListItem sx={{ mb: 1 }}>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '50%', 
                        bgcolor: '#3b82f6', 
                        color: 'white', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      1
                    </Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={t(`booking:confirmation.nextSteps.variants.${nextStepsVariant}.step1.title`)}
                    secondary={t(`booking:confirmation.nextSteps.variants.${nextStepsVariant}.step1.description`)}
                    sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium', color: '#1f2937' } }}
                  />
                </ListItem>
                
                <ListItem sx={{ mb: 1 }}>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '50%', 
                        bgcolor: '#10b981', 
                        color: 'white', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      2
                    </Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={t(`booking:confirmation.nextSteps.variants.${nextStepsVariant}.step2.title`)}
                    secondary={t(`booking:confirmation.nextSteps.variants.${nextStepsVariant}.step2.description`)}
                    sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium', color: '#1f2937' } }}
                  />
                </ListItem>
                
                <ListItem sx={{ mb: 1 }}>
                  <ListItemIcon>
                    <Box 
                      sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '50%', 
                        bgcolor: '#f59e0b', 
                        color: 'white', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontWeight: 'bold'
                      }}
                    >
                      3
                    </Box>
                  </ListItemIcon>
                  <ListItemText 
                    primary={t(`booking:confirmation.nextSteps.variants.${nextStepsVariant}.step3.title`)}
                    secondary={t(`booking:confirmation.nextSteps.variants.${nextStepsVariant}.step3.description`)}
                    sx={{ '& .MuiListItemText-primary': { fontWeight: 'medium', color: '#1f2937' } }}
                  />
                </ListItem>
              </List>
            </AccordionDetails>
          </Accordion>

          {/* Actions rapides */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 2, 
            justifyContent: 'center',
            mt: 3,
            mb: 2
          }}>
            <button
              onClick={() => navigate('/reservations-renter')}
              className="action-btn"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(45deg, #3b82f6 30%, #1e40af 90%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'medium',
                transition: 'all 0.3s ease'
              }}
            >
              <Person style={{ fontSize: '1.25rem' }} />
              {t('booking:confirmation.actions.myReservations')}
            </button>
            
            <button
              onClick={() => navigate('/search')}
              className="action-btn"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(45deg, #6b7280 30%, #374151 90%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'medium',
                transition: 'all 0.3s ease'
              }}
            >
              <LocationOn style={{ fontSize: '1.25rem' }} />
              {t('booking:confirmation.actions.continueShopping')}
            </button>
            
            <button
              onClick={() => window.print()}
              className="action-btn print-btn"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'white',
                color: '#6b7280',
                border: '2px solid #d1d5db',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'medium',
                transition: 'all 0.3s ease'
              }}
            >
              <Print style={{ fontSize: '1.25rem' }} />
              {t('booking:confirmation.actions.print')}
            </button>
          </Box>

        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
